import type {
  AdvisorOverview,
  AuthStatus,
  AzureTenant,
  ComparisonMode,
  CostDriverOverview,
  CostOverview,
  CreditOverview,
  DisplayCurrency,
  GovernanceOverview,
  InsightsResponse,
  MicrosoftTenantDiscovery,
  OrphanedResourcesOverview,
  PortfolioOverview,
  PortfolioTenantEntry,
  ReservationOverview,
  ResourcePerformanceOverview,
  ServiceCostOverview,
  ServicePrincipalCredentials,
  TagCostOverview,
  TagKeysResponse,
  TagValuesResponse,
  Timeframe
} from "./types";

export class ApiClientError extends Error {
  public readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
  }
}

let forcedRefreshDepth = 0;

export async function withForcedAzureRefresh<T>(action: () => Promise<T>): Promise<T> {
  forcedRefreshDepth += 1;

  try {
    return await action();
  } finally {
    forcedRefreshDepth -= 1;
  }
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = (await response.clone().json()) as { error?: string; details?: string };
    return body.details || body.error || `Request failed with HTTP ${response.status}`;
  } catch {
    const text = await response.text();
    return text || `Request failed with HTTP ${response.status}`;
  }
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method?.toUpperCase() || "GET";
  const requestPath =
    forcedRefreshDepth > 0 && method === "GET"
      ? `${path}${path.includes("?") ? "&" : "?"}refresh=true`
      : path;
  const response = await fetch(requestPath, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers || {})
    }
  });

  if (!response.ok) {
    throw new ApiClientError(response.status, await parseError(response));
  }

  return (await response.json()) as T;
}

export function getAuthStatus(): Promise<AuthStatus> {
  return requestJson<AuthStatus>("/api/auth");
}

export function loginServicePrincipal(credentials: ServicePrincipalCredentials): Promise<AuthStatus> {
  return requestJson<AuthStatus>("/api/auth", {
    method: "POST",
    body: JSON.stringify({
      mode: "servicePrincipal",
      ...credentials
    })
  });
}

export function discoverMicrosoftTenants(accessToken: string): Promise<MicrosoftTenantDiscovery> {
  return requestJson<MicrosoftTenantDiscovery>("/api/microsoft/tenants", {
    method: "POST",
    body: JSON.stringify({ accessToken })
  });
}

export function loginMicrosoftTenant(accessToken: string, tenantId: string): Promise<AuthStatus> {
  return requestJson<AuthStatus>("/api/auth", {
    method: "POST",
    body: JSON.stringify({
      mode: "microsoft",
      accessToken,
      tenantId
    })
  });
}

export async function getAvailableTenants(): Promise<AzureTenant[]> {
  const response = await requestJson<{ tenants: AzureTenant[] }>("/api/tenants");
  return response.tenants;
}

export function logout(): Promise<AuthStatus> {
  return requestJson<AuthStatus>("/api/auth", {
    method: "DELETE"
  });
}

export function getCostOverview(
  timeframe: Timeframe,
  currency: DisplayCurrency,
  range?: { from?: string; to?: string },
  forceRefresh = false,
  comparisonMode: ComparisonMode = "previousPeriod"
): Promise<CostOverview> {
  const search = new URLSearchParams({ timeframe, currency, comparisonMode });

  if (timeframe === "Custom" && range?.from && range?.to) {
    search.set("from", range.from);
    search.set("to", range.to);
  }

  if (forceRefresh) {
    search.set("refresh", "true");
  }

  return requestJson<CostOverview>(`/api/costs?${search.toString()}`);
}

export function getCostDrivers(
  timeframe: Timeframe,
  currency: DisplayCurrency,
  range?: { from?: string; to?: string },
  comparisonMode: ComparisonMode = "previousPeriod"
): Promise<CostDriverOverview> {
  const search = new URLSearchParams({ timeframe, currency, comparisonMode });

  if (timeframe === "Custom" && range?.from && range?.to) {
    search.set("from", range.from);
    search.set("to", range.to);
  }

  return requestJson<CostDriverOverview>(`/api/cost-drivers?${search.toString()}`);
}

export function getInsights(currency: DisplayCurrency): Promise<InsightsResponse> {
  return requestJson<InsightsResponse>(`/api/insights?currency=${currency}`);
}

export function getCreditOverview(currency: DisplayCurrency, forceRefresh = false): Promise<CreditOverview> {
  const search = new URLSearchParams({ currency });

  if (forceRefresh) {
    search.set("refresh", "true");
  }

  return requestJson<CreditOverview>(`/api/credits?${search.toString()}`);
}

export function getServiceCosts(currency: DisplayCurrency): Promise<ServiceCostOverview> {
  return requestJson<ServiceCostOverview>(`/api/service-costs?currency=${currency}`);
}

export function getTagCosts(
  tagKey: string,
  tagValue: string,
  currency: DisplayCurrency
): Promise<TagCostOverview> {
  const search = new URLSearchParams({ tagKey, currency });

  if (tagValue) {
    search.set("tagValue", tagValue);
  }

  return requestJson<TagCostOverview>(`/api/tag-costs?${search.toString()}`);
}

export function getTagKeys(): Promise<TagKeysResponse> {
  return requestJson<TagKeysResponse>("/api/tag-keys");
}

export function getTagValues(tagKey: string): Promise<TagValuesResponse> {
  const search = new URLSearchParams({ tagKey });
  return requestJson<TagValuesResponse>(`/api/tag-values?${search.toString()}`);
}

export function getResourcePerformance(hours = 24): Promise<ResourcePerformanceOverview> {
  const search = new URLSearchParams({ hours: String(hours) });
  return requestJson<ResourcePerformanceOverview>(`/api/resource-performance?${search.toString()}`);
}

export function getAdvisorOverview(currency: DisplayCurrency): Promise<AdvisorOverview> {
  return requestJson<AdvisorOverview>(`/api/advisor?currency=${currency}`);
}

export function getReservationOverview(currency: DisplayCurrency): Promise<ReservationOverview> {
  return requestJson<ReservationOverview>(`/api/reservations?currency=${currency}`);
}

export function getOrphanedResources(currency: DisplayCurrency): Promise<OrphanedResourcesOverview> {
  return requestJson<OrphanedResourcesOverview>(`/api/orphaned-resources?currency=${currency}`);
}

export function getGovernanceOverview(currency: DisplayCurrency): Promise<GovernanceOverview> {
  return requestJson<GovernanceOverview>(`/api/governance?currency=${currency}`);
}

export function getPortfolioOverview(
  tenants: PortfolioTenantEntry[],
  currency: DisplayCurrency
): Promise<PortfolioOverview> {
  return requestJson<PortfolioOverview>("/api/portfolio", {
    method: "POST",
    body: JSON.stringify({ tenants, currency })
  });
}

export async function downloadPortfolioExport(tenants: PortfolioTenantEntry[], currency: DisplayCurrency): Promise<void> {
  const response = await fetch("/api/portfolio/export", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tenants, currency })
  });

  if (!response.ok) {
    throw new ApiClientError(response.status, await parseError(response));
  }

  await triggerBlobDownload(response, `expense-analyzer-portfolio-${currency.toLowerCase()}.csv`);
}

export async function downloadExport(format: "csv" | "xlsx", currency: DisplayCurrency): Promise<void> {
  const search = new URLSearchParams({ format, currency });
  const response = await fetch(`/api/export?${search.toString()}`, {
    credentials: "include"
  });

  if (!response.ok) {
    throw new ApiClientError(response.status, await parseError(response));
  }

  await triggerBlobDownload(response, `expense-analyzer-subscriptions.${format === "csv" ? "csv" : "xls"}`);
}

async function triggerBlobDownload(response: Response, fallbackFileName: string): Promise<void> {
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") || "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const fileName = match?.[1] || fallbackFileName;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
